import { createContext, useState, useEffect, useContext } from 'react';
import { getAuth, onAuthStateChanged, signOut, signInWithCustomToken } from 'firebase/auth';
import { get, remove, set } from 'packages/utils/storage';
import { apiCallable } from '@src/util/cloud-functions/apiClient';
import { API_OPERATIONS } from '@src/util/cloud-functions/operations';

import { DT_SESSION_CLOSED_KEY } from '../constants';

export const AuthContext = createContext();
const auth = getAuth();
export const FIREBASE_UID_ST = 'firebaseUID';

let isLocalSignOutInProgress = false;

export const getIsLocalSignOutInProgress = () => isLocalSignOutInProgress;

const clearDtStorage = () => {
  remove(FIREBASE_UID_ST);
};

const notifyDtSessionClosed = () => {
  localStorage.setItem(DT_SESSION_CLOSED_KEY, String(Date.now()));
  localStorage.removeItem(DT_SESSION_CLOSED_KEY);
};

export const signOutDtLocal = async () => {
  try {
    await signOut(auth);
    clearDtStorage();
  } catch (error) {
    console.error('Error signing out:', error);
  }
};

export const signOutDt = async () => {
  isLocalSignOutInProgress = true;
  try {
    await signOut(auth);
    clearDtStorage();
    notifyDtSessionClosed();
    window.location.href = '/dt';
  } catch (error) {
    isLocalSignOutInProgress = false;
    console.error('Error signing out:', error);
  }
};

export const signInDt = async ({ email, code }) => {
  try {
    const httpRef = apiCallable(API_OPERATIONS.SEND_CODE_ACCESS_DT);
    const res = await httpRef({ email, code: code?.toUpperCase() });
    const token = res.data.token;

    if (!token || typeof token !== 'string') throw new Error('Invalid token');

    if (token) {
      const customResponse = await signInWithCustomToken(auth, token);
      const uid = customResponse?.user?.uid;
      set(FIREBASE_UID_ST, uid); // NOTE: This refer to userId
      window.location.href = '/dt';
    }
  } catch (error) {
    throw error;
  }
};

//NOTE: This provider is used to check if the user is logged in
/*
  Practically if firebaseUID is not in the local storage, we must sign out the user
*/
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, authUser => {
      const firebaseUID = get(FIREBASE_UID_ST);
      if (authUser && firebaseUID) {
        setUser(authUser);
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return <AuthContext.Provider value={{ user, loading }}>{children}</AuthContext.Provider>;
};

export const useAuthTimeTracker = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthTimeTracker must be used within a AuthProvider');
  }
  return context;
};

export default AuthProvider;
